// ======================= BTGO CONFIG =======================
const BTGOCONFIG = {
  fourOzSkus: ["TTFC-4OZ", "UFC-4OZ", "CAR-4OZ", "UFC-EOF-4OZ"],
  debug: false,
  giftImageUrl:
    "/cdn/shop/files/0e015eefd62879b5b924afe07594322cc68e0f72_1_200x.png?v=1759607793",
  GIFT_VARIANT_ID: "46940001599706",
  retryAttempts: 3,
  retryBaseDelay: 300,
  checkoutTimeout: 8000,
};

// ======================= LOGGING =======================

function log(...args) {
  if (BTGOCONFIG.debug) {
    console.log("[BTGO v4]", ...args);
  }
}

// ======================= HELPER FUNCTIONS =======================

function getGiftVariantId() {
  if (BTGOCONFIG.GIFT_VARIANT_ID) {
    return Number(BTGOCONFIG.GIFT_VARIANT_ID);
  }

  const hostEl = document.querySelector("[data-gift-variant-id]");
  if (hostEl?.dataset?.giftVariantId) {
    return Number(hostEl.dataset.giftVariantId);
  }

  const progressEl = document.querySelector(".pGiftProgressTitle");
  if (progressEl?.dataset?.id) {
    return Number(progressEl.dataset.id);
  }

  return 0;
}

function isCheckoutPage() {
  const path = location.pathname || "";
  return path.indexOf("/checkout") === 0 || path.indexOf("/checkouts/") > -1;
}

// ======================= RETRY-ENABLED API =======================

function isRetryable(status) {
  return status === 429 || status >= 500;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithRetry(fetchFn, label) {
  let lastError;
  for (let attempt = 0; attempt < BTGOCONFIG.retryAttempts; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;
      const retryable = !error.httpStatus || isRetryable(error.httpStatus);
      if (!retryable || attempt === BTGOCONFIG.retryAttempts - 1) {
        throw error;
      }
      const delay = BTGOCONFIG.retryBaseDelay * Math.pow(2, attempt);
      log(
        `${label} failed (attempt ${attempt + 1}), retrying in ${delay}ms:`,
        error.message,
      );
      await sleep(delay);
    }
  }
  throw lastError;
}

async function getCart() {
  return fetchWithRetry(async () => {
    const response = await fetch("/cart.js", {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!response.ok) {
      const err = new Error(`/cart.js ${response.status}`);
      err.httpStatus = response.status;
      throw err;
    }
    return await response.json();
  }, "getCart");
}

async function apiPost(url, payload) {
  return fetchWithRetry(async () => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const msg = data?.description || data?.message || response.statusText;
      const err = new Error(msg || `HTTP ${response.status}`);
      err.httpStatus = response.status;
      log("Cart API error:", url, response.status, msg);
      throw err;
    }

    return data;
  }, `apiPost(${url})`);
}

// ======================= GIFT DETECTION =======================

function isGiftLine(item) {
  if (!item) return false;

  const giftId = getGiftVariantId();
  if (giftId && item.variant_id === giftId) {
    return true;
  }

  const props = item.properties || {};
  const msg = String(props.Message || "").toLowerCase().trim();
  const legacy = String(props.Gift || "").toLowerCase().trim();

  return msg === "gift" || legacy === "true" || legacy === "gift";
}

// ======================= 4OZ COUNTING =======================

function countFourOunceItems(cart) {
  let count = 0;

  (cart.items || []).forEach((item) => {
    if (isGiftLine(item)) {
      log(`Skipping gift item: ${item.product_title}`);
      return;
    }

    const sellingPlanName =
      item.selling_plan_allocation?.selling_plan?.name || "";
    if (sellingPlanName.toLowerCase().includes("try before you buy")) {
      log(`Skipping TBYB item: ${item.product_title}`);
      return;
    }

    const productTitle = String(item.product_title || "").toLowerCase().trim();
    const variantTitle = String(item.variant_title || "").toLowerCase().trim();
    const combinedTitle = `${productTitle} ${variantTitle}`;

    // PRIORITY 1: Check specific SKUs (most reliable)
    if (BTGOCONFIG.fourOzSkus.length > 0 && item.sku) {
      const itemSku = String(item.sku).trim().toUpperCase();
      const matchesSku = BTGOCONFIG.fourOzSkus.some(
        (sku) => String(sku).trim().toUpperCase() === itemSku,
      );
      if (matchesSku) {
        count += item.quantity;
        log(
          `Matched 4oz SKU: ${item.sku} (${item.product_title}), qty: ${item.quantity}`,
        );
        return;
      }
    }

    // PRIORITY 2: Flare Care Duo counts as 2
    if (productTitle.includes("flare care duo")) {
      count += item.quantity * 2;
      log(
        `Matched Flare Care Duo: ${item.product_title}, counting as ${item.quantity * 2}`,
      );
      return;
    }

    // PRIORITY 3: Fallback — title/variant pattern match
    const isFourOz = /\b4\s*-?\s*oz\b|\b4\s*ounce\b/.test(combinedTitle);
    if (isFourOz) {
      count += item.quantity;
      log(
        `Matched 4oz pattern: ${item.product_title} - ${item.variant_title}, qty: ${item.quantity}`,
      );
    }
  });

  log(`Total 4oz items: ${count}`);
  return count;
}

// ======================= GIFT MANAGEMENT =======================

// Remove gift lines SEQUENTIALLY (Shopify cart API is not concurrency-safe)
async function removeAllGifts(cart) {
  const giftId = getGiftVariantId();
  if (!giftId) return cart;

  const giftLines = (cart.items || []).filter(
    (item) => item.variant_id === giftId,
  );

  if (giftLines.length === 0) {
    log("No gift items to remove");
    return cart;
  }

  log(`Removing ${giftLines.length} gift line(s) sequentially`);
  for (const line of giftLines) {
    await apiPost("/cart/change.js", { id: line.key, quantity: 0 });
  }

  return await getCart();
}

async function addGift() {
  const giftId = getGiftVariantId();
  if (!giftId) {
    log("ERROR: Gift variant ID not found");
    return;
  }

  log("Adding gift item to cart");
  await apiPost("/cart/add.js", {
    items: [{ id: giftId, quantity: 1, properties: { Message: "Gift" } }],
  });
}

async function ensureOneGift(cart) {
  const giftId = getGiftVariantId();
  if (!giftId) return cart;

  const giftLines = (cart.items || []).filter(
    (item) => item.variant_id === giftId,
  );

  // Already have exactly one gift line with quantity 1 and correct properties — done
  if (
    giftLines.length === 1 &&
    giftLines[0].quantity === 1 &&
    isGiftLine(giftLines[0])
  ) {
    log("Gift already correctly in cart");
    return cart;
  }

  // Otherwise, clean up and add fresh
  if (giftLines.length > 0) {
    cart = await removeAllGifts(cart);
  }
  await addGift();

  return await getCart();
}

// ======================= MAIN PROMOTION LOGIC =======================

let processing = false;

async function finalizePromotion() {
  if (processing) {
    log("Already processing, skipping");
    return;
  }

  processing = true;

  try {
    const giftId = getGiftVariantId();
    if (!giftId) {
      log("ERROR: Gift variant ID not configured");
      return;
    }

    let cart = await getCart();
    const fourOzCount = countFourOunceItems(cart);
    const qualified = fourOzCount >= 2;

    log(`Qualification: ${fourOzCount} 4oz items, qualified=${qualified}`);

    if (!qualified) {
      await removeAllGifts(cart);
      log("Gift removed (not qualified)");
    } else {
      cart = await ensureOneGift(cart);
      log("Gift ensured in cart");
    }

    // Brief settle time for Shopify's backend
    await sleep(100);
  } finally {
    processing = false;
  }
}

// Wrap finalizePromotion with a timeout — user is NEVER stuck
function finalizeWithTimeout() {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      log("WARNING: finalizePromotion timed out, proceeding to checkout");
      processing = false;
      resolve();
    }, BTGOCONFIG.checkoutTimeout);

    finalizePromotion()
      .then(() => {
        clearTimeout(timer);
        resolve();
      })
      .catch((error) => {
        clearTimeout(timer);
        log("ERROR in finalizePromotion:", error.message);
        // Resolve anyway — never block checkout
        resolve();
      });
  });
}

// ======================= GIFT NUDGE UI =======================

function createGiftNudgeHTML(fourOzCount) {
  const progressPercent = Math.min((fourOzCount / 2) * 100, 100);
  const progressColor =
    fourOzCount >= 2 ? "rgb(71, 174, 0)" : "rgba(145, 145, 145, 1)";

  let message = "";
  if (fourOzCount >= 2) {
    message = "<span>FREE GIFT</span> is added to your order 🎁";
  } else if (fourOzCount === 1) {
    message = "One more 4oz to get a <span>FREE GIFT</span> 🎁";
  } else {
    message = "Add two 4oz to get a <span>FREE GIFT</span> 🎁";
  }

  return `
    <div class="pGiftCtn inside">
      <div class="pGiftDiv reverse">
        <div class="pGiftInfo">
          <div class="pGiftImageCtn">
            <img src="${BTGOCONFIG.giftImageUrl}" alt="Universal Flare Care Gift" class="pGiftImage" loading="lazy">
          </div>
          <div class="pGiftTitleCtn">
            <p class="pGiftMessage">${message}</p>
            <div class="pGiftProgressCtn">
              <div class="pGiftProgressRail">
                <div class="pGiftProgress" style="transform: scaleX(${progressPercent / 100}); background: ${progressColor};"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

async function updateGiftNudge() {
  const container = document.querySelector(".giftNudgeCartCtn");
  if (!container) {
    log("Gift nudge container not found");
    return;
  }

  const cart = await getCart();
  const fourOzCount = countFourOunceItems(cart);

  container.innerHTML = createGiftNudgeHTML(fourOzCount);
  log(`Gift nudge updated: ${fourOzCount} 4oz items`);
}

// ======================= CART DRAWER INTEGRATION =======================

function getCartDrawer() {
  window.theme = window.theme || {};
  if (!window.theme.cartDrawer && window.theme.CartDrawer) {
    window.theme.cartDrawer = new window.theme.CartDrawer();
  }
  return window.theme.cartDrawer;
}

function rebuildCartDrawer() {
  try {
    const drawer = getCartDrawer();
    if (drawer?.cartForm?.buildCart) {
      drawer.cartForm.buildCart();
    }
  } catch (error) {
    log("Error rebuilding cart drawer:", error);
  }
}

// ======================= CHECKOUT INTERCEPTION (Event Delegation) =======================

// Walk up the DOM to find if the clicked element is inside a checkout link/button
function findCheckoutAncestor(el) {
  let node = el;
  for (let i = 0; i < 6 && node && node !== document.body; i++) {
    // Dedicated drawer checkout class
    if (node.classList?.contains("drawerCheckout")) return node;

    // Exact /checkout href (NOT partial matches like /checkout/thank-you)
    if (node.tagName === "A") {
      const href = node.getAttribute("href") || "";
      if (href === "/checkout" || href === "/checkout/") return node;
    }

    // Buttons with checkout name
    if (node.tagName === "BUTTON" || node.tagName === "INPUT") {
      const name = node.getAttribute("name") || "";
      if (name === "checkout") return node;
    }

    node = node.parentElement;
  }

  return null;
}

function attachCheckoutIntercept() {
  // Click delegation on document — survives ANY number of DOM re-renders
  document.addEventListener(
    "click",
    async (e) => {
      const checkoutEl = findCheckoutAncestor(e.target);
      if (!checkoutEl) return;

      log("Checkout click intercepted");
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      checkoutEl.style.pointerEvents = "none";
      checkoutEl.style.opacity = "0.6";
      checkoutEl.textContent = "Processing...";

      try {
        await finalizeWithTimeout();
      } catch (_) {
        // Should never reach here, but never block checkout
        log("Unexpected error, proceeding to checkout");
      }

      log("Redirecting to checkout");
      window.location.href = "/checkout";
    },
    true, // useCapture — fires before any other click handlers
  );

  // Also intercept form submissions to /checkout
  document.addEventListener(
    "submit",
    async (e) => {
      const form = e.target;
      if (!form || form.tagName !== "FORM") return;

      const action = form.getAttribute("action") || "";
      if (action !== "/checkout" && action !== "/checkout/") return;

      log("Checkout form submit intercepted");
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const submitBtn = form.querySelector('[type="submit"], button');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Processing...";
      }

      try {
        await finalizeWithTimeout();
      } catch (_) {
        log("Unexpected error, proceeding to checkout");
      }

      log("Redirecting to checkout");
      window.location.href = "/checkout";
    },
    true,
  );

  log("Checkout intercept (delegated) attached");
}

// ======================= INITIALIZATION =======================

function init() {
  if (isCheckoutPage()) {
    log("Checkout page detected, skipping init");
    return;
  }

  log("Initializing BTGO v4");
  attachCheckoutIntercept();
  log("BTGO v4 initialized");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
